import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, inr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const BLANK = { name: "", start_date: "", end_date: "", amount_per_jeep: 2000, is_active: true };
const input = "h-10 rounded-md border border-[#E2DACF] bg-white px-3 text-sm w-full";

function SupplementForm({ initial, onDone }) {
  const [f, setF] = useState(initial);
  const save = async () => {
    try {
      const body = { ...f, amount_per_jeep: Number(f.amount_per_jeep) };
      if (f.id) await api.put(`/admin/supplements/${f.id}`, body); else await api.post("/admin/supplements", body);
      toast.success("Supplement saved"); onDone();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="bg-white ring-1 ring-black/5 rounded-2xl p-5 grid gap-3 md:grid-cols-5 items-end" data-testid="sup-form">
      <label className="text-xs text-[#5C6B63] md:col-span-2">Name<input className={input} value={f.name} placeholder="Diwali peak" onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sup-name"/></label>
      <label className="text-xs text-[#5C6B63]">From<input type="date" className={input} value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="sup-start"/></label>
      <label className="text-xs text-[#5C6B63]">To<input type="date" className={input} value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="sup-end"/></label>
      <label className="text-xs text-[#5C6B63]">₹ per jeep<input type="number" min="0" className={input} value={f.amount_per_jeep} onChange={(e) => setF({ ...f, amount_per_jeep: e.target.value })} data-testid="sup-amount"/></label>
      <div className="md:col-span-5 flex items-center gap-3">
        <Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid="sup-active"/><span className="text-sm">Active</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => onDone(true)} className="jt-pill-outline text-sm" data-testid="sup-cancel">Cancel</button>
          <button onClick={save} disabled={!f.name || !f.start_date || !f.end_date} className="jt-pill-primary text-sm disabled:opacity-40" data-testid="sup-save">Save</button>
        </div>
      </div>
    </div>
  );
}

export default function SupplementManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/supplements").then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await api.delete(`/admin/supplements/${s.id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="mt-6 space-y-5" data-testid="supplements">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-[#5C6B63] max-w-2xl">Extra charge per jeep on peak dates, added to every safari (morning and evening). If dates overlap, only the highest supplement applies. Guests see it as a separate line in the checkout price breakdown, email and voucher. Existing bookings are not changed.</p>
        {!editing && <button onClick={() => setEditing(BLANK)} className="jt-pill-primary text-sm" data-testid="sup-add"><Plus size={14}/> Add supplement</button>}
      </div>
      {editing && <SupplementForm initial={editing} onDone={() => { setEditing(null); load(); }}/>}
      <table className="w-full text-sm bg-white ring-1 ring-black/5 rounded-2xl overflow-hidden">
        <thead className="bg-[#F5F0EB] text-left text-xs text-[#5C6B63]"><tr><th className="p-3">Name</th><th>Dates</th><th>Per jeep</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="p-4 text-[#5C6B63]" data-testid="sup-empty">No supplements yet.</td></tr>}
          {rows.map((s) => (
            <tr key={s.id} className="border-t border-[#EFE9E1]" data-testid={`sup-row-${s.id}`}>
              <td className="p-3 font-medium">{s.name}</td>
              <td>{s.start_date} → {s.end_date}</td>
              <td className="font-mono">+{inr(s.amount_per_jeep)}</td>
              <td><span className="inline-flex items-center gap-2"><Switch checked={s.is_active} onCheckedChange={async (v) => { try { await api.put(`/admin/supplements/${s.id}`, { ...s, is_active: v }); load(); } catch (e) { toast.error(formatErr(e)); } }} data-testid={`sup-toggle-${s.id}`}/>
                <span className={`text-xs px-2 py-0.5 rounded-full ${s.is_active ? "bg-[#DCE8DF] text-[#1E3A2B]" : "bg-[#EFE9E1] text-[#5C6B63]"}`}>{s.is_active ? "active" : "off"}</span></span></td>
              <td className="text-right pr-3 space-x-3">
                <button onClick={() => setEditing(s)} data-testid={`sup-edit-${s.id}`}><Pencil size={14}/></button>
                <button onClick={() => remove(s)} data-testid={`sup-delete-${s.id}`}><Trash2 size={14} className="text-[#C86D51]"/></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
